export const catalogueAudit = {
  schemaVersion: "1.0.0",
  title: "TrypCell Atlas catalogue content audit",
  auditedAt: "2026-07-27",
  policy: {
    inclusion:
      "A catalogue record must resolve to a primary paper and a public raw-data repository. Cell totals must state their counting boundary.",
    evidenceOrder: [
      "Version-of-record paper",
      "Author-deposited processed files",
      "Repository metadata and raw-read manifests",
      "Portal-derived summaries, explicitly labelled as derived",
    ],
    prohibited:
      "No simulated cells, invented expression values, inferred sample labels or reconstructed author results are presented as source data.",
  },
  totals: {
    studies: 6,
    catalogueCellEntries: 57674,
    correctedClaims: 4,
    scopedClaims: 6,
  },
  supplementaryEvidenceLayers: [
    {
      id: "chagasdb-host-response",
      label: "ChagasDB",
      role: "Host-response evidence kept separate from parasite single-cell expression.",
      verdict: "source-verified-not-republished",
      paperSnapshot: {
        curatedPapers: 193,
        publicationCoverage: "1995–2022",
        mammalianHostSpecies: 5,
        trypanosomaCruziStrains: 24,
      },
      paperUrl: "https://pmc.ncbi.nlm.nih.gov/articles/PMC10205463/",
      databaseUrl: "https://chagasdb.tagc.univ-amu.fr/",
      boundary:
        "The figures are the paper-reported database snapshot. TrypCell links to ChagasDB but does not bulk-republish its records or merge host molecular changes into parasite expression matrices.",
    },
  ],
  datasets: [
    {
      id: "brg-2021-bsf",
      label: "Briggs et al. 2021",
      species: "Trypanosoma brucei brucei",
      verdict: "verified-with-scope",
      verdictZh: "已核实，补充统计口径",
      cells: 10894,
      cellBoundary:
        "Sum of three author-deposited filtered matrices: WT_01 5,321 + WT_02 3,278 + ZC3H20_KO 2,295. The WT trajectory alone contains 8,599 cells.",
      technology:
        "Chromium Single Cell 3′ GEM, Library & Gel Bead Kit v3 (10x Genomics).",
      evidence: [
        {
          label: "Primary paper",
          url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC8421343/",
          supports: "Library chemistry, experimental design and PRJEB41744.",
        },
        {
          label: "Zenodo 5163554",
          url: "https://zenodo.org/records/5163554",
          supports:
            "Three filtered count matrices and analysis code; archive MD5 11e3f460d4fbc8ac43c7cc1806e4e344.",
        },
        {
          label: "ENA PRJEB41744",
          url: "https://www.ebi.ac.uk/ena/browser/view/PRJEB41744",
          supports: "Raw sequencing accession.",
        },
        {
          label: "TrypCell provenance audit",
          url: "/datasets/prjeb41744/",
          supports:
            "ENA BAM/BAI manifest, matrix dimensions, checksums and author-atlas conversion boundary.",
        },
      ],
      auditNote:
        "The catalogue total deliberately includes WT and knockout matrices and is not labelled as the WT-only atlas size. The paper spells the strain EATRO 1125 AnTa1.1 90:13; common AnTat1.1 wording is retained only as an alias.",
    },
    {
      id: "how-2022-tsetse",
      label: "Howick et al. 2022",
      species: "Trypanosoma brucei brucei",
      verdict: "verified-with-scope",
      verdictZh: "已核实，补充统计口径",
      cells: 388,
      cellBoundary:
        "Fly-derived parasite transcriptomes: 78 midgut + 34 proventriculus + 276 salivary gland. The paper additionally reports 46 in-vitro procyclic single cells used to validate the protocol; those are not included in the catalogue total.",
      technology: "Modified Smart-seq2.",
      evidence: [
        {
          label: "Primary paper",
          url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC8939820/",
          supports:
            "Cell totals by tissue, modified Smart-seq2 method and repository identifiers.",
        },
        {
          label: "ENA ERP132258",
          url: "https://www.ebi.ac.uk/ena/browser/view/ERP132258",
          supports: "Raw sequencing accession.",
        },
        {
          label: "Zenodo 6047732",
          url: "https://zenodo.org/records/6047732",
          supports: "Author-deposited processed results.",
        },
        {
          label: "TrypCell provenance audit",
          url: "/datasets/erp132258/",
          supports:
            "Paper-versus-repository counting boundary, ENA run/file totals, processed matrix dimensions, checksums and deposited UMAP coverage.",
        },
      ],
      auditNote:
        "The 388-cell value describes the QC-passing fly-stage atlas rather than every single-cell library reported in the study. J10 is MCRO/ZM/73/J10 and 1738 is MOVS/KE/70/EATRO 1738; DP is a sorting group, not a strain. C5 is predominantly J10 and C6 predominantly 1738, so strain and stage effects may be confounded. The current ENA report exposes 468 runs, while the paper reports 515 sequenced tsetse parasites; these counts are retained separately.",
    },
    {
      id: "brg-2023-replicative",
      label: "Briggs et al. 2023",
      species: "Trypanosoma brucei brucei",
      verdict: "verified",
      verdictZh: "已核实",
      cells: 12006,
      cellBoundary:
        "Rows in the author phase tables, excluding headers: 4,366 bloodstream-form + 7,640 procyclic-form cells.",
      technology:
        "Chromium Single Cell 3′ chemistry version 3.1 (10x Genomics).",
      evidence: [
        {
          label: "Primary paper",
          url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC10212563/",
          supports:
            "Chromium v3.1 chemistry, study design and PRJEB58781.",
        },
        {
          label: "Zenodo 7508131",
          url: "https://zenodo.org/records/7508131",
          supports:
            "BSF_phase.csv (MD5 0bae6b3b7b7e7e0b4819397aad89d9b4) and PCF_phase.csv (MD5 8b10d233bd4f3b2de287d5dc6e0415bf).",
        },
        {
          label: "ENA PRJEB58781",
          url: "https://www.ebi.ac.uk/ena/browser/view/PRJEB58781",
          supports: "Raw sequencing accession.",
        },
        {
          label: "TrypCell provenance audit",
          url: "/datasets/prjeb58781/",
          supports:
            "ENA run/file totals, Zenodo inventory and checksums, author UMAP/MNN conversion, phase counts and expression-semantics boundary.",
        },
      ],
      auditNote:
        "The total was independently reproduced from the two author-deposited cell-level phase tables. The experimental lines are Lister 427-derived. Reads were mapped to T. brucei WT427 2018 and L. major Friedlin references with Cell Ranger 7 so L. major cells and mixed-species multiplets could be removed. BSF and PCF coordinates remain separate because the authors computed them in separate embedding spaces.",
    },
    {
      id: "reu-2023-skin",
      label: "Reuter et al. 2023",
      species: "Trypanosoma brucei brucei",
      verdict: "corrected",
      verdictZh: "已纠正",
      cells: 170,
      cellBoundary:
        "Parasite transcriptomes passing quality control: 44 metacyclic forms + 24 at 4 h + 26 at 12 h + 23 at 24 h + 22 at 7 d + 31 bloodstream forms.",
      technology:
        "SMART-Seq v4 Ultra Low Input RNA Kit followed by Nextera XT library preparation. The Figure 4 caption describes the workflow as Smart-seq2/Nextera.",
      evidence: [
        {
          label: "Primary paper",
          url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC10667367/",
          supports:
            "QC-passed cell counts, SMART-Seq v4/Nextera XT workflow and study design.",
        },
        {
          label: "GEO GSE174198",
          url: "https://www.ncbi.nlm.nih.gov/geo/query/acc.cgi?acc=GSE174198",
          supports: "Raw and supplementary data record.",
        },
        {
          label: "TrypCell provenance audit",
          url: "/datasets/gse174198/",
          supports:
            "GEO/SRA manifests, processed-table dimensions and checksums, three paper-specified exclusions, expression-unit separation and unavailable-PCA boundary.",
        },
      ],
      auditNote:
        "The exact kit name is preserved from the Methods and GEO record. The paper's Figure 4 caption uses Smart-seq2/Nextera terminology, so the portal records this nomenclature difference instead of declaring either source wording invalid.",
    },
    {
      id: "lai-2026-cruzi",
      label: "Laidlaw et al. 2026",
      species: "Trypanosoma cruzi",
      verdict: "verified-with-boundary",
      verdictZh: "已核实，注明数据边界",
      cells: 31065,
      cellBoundary:
        "Valid cells retained after author QC across the lifecycle atlas; the paper reports 15,321 genes detected in total.",
      technology:
        "Chromium Next GEM Single Cell 3′ Reagent Kit v3.1 and v3.1 Dual Index (10x Genomics).",
      evidence: [
        {
          label: "Version-of-record paper",
          url: "https://www.nature.com/articles/s41467-026-73098-w",
          supports:
            "31,065 valid cells, 15,321 detected genes, chemistry, reference and data availability.",
        },
        {
          label: "BioStudies E-MTAB-14406",
          url: "https://www.ebi.ac.uk/biostudies/arrayexpress/studies/E-MTAB-14406",
          supports:
            "Official IDF/SDRF metadata and links to ENA raw reads.",
        },
        {
          label: "Author atlas",
          url: "https://cellatlas-cxg.mvls.gla.ac.uk/view/Trypanosoma_cruzi_inVitro_cellatlas.h5ad/",
          supports: "Author UMAP, cell metadata and queryable expression view.",
        },
        {
          label: "TrypCell record audit",
          url: "/datasets/e-mtab-14406/",
          supports:
            "8 source samples, 8 experiments, 25 runs and 79 FASTQ entries matched exactly to the official SDRF.",
        },
      ],
      auditNote:
        "The experimental strain is Silvio X10/7 A1; Dm28c 2018 is the mapping reference. BioStudies exposes metadata and raw-read links but no processed count matrix. The author atlas serves a processed AnnData view; TrypCell does not present that active scaled matrix as raw counts.",
    },
    {
      id: "inc-2026-cruzi",
      label: "Inchausti et al. 2026",
      species: "Trypanosoma cruzi",
      verdict: "verified-with-scope",
      verdictZh: "已核实，补充统计口径",
      cells: 3151,
      cellBoundary:
        "Final retained singlets. The paper first reports 3,192 cells after quality filtering, then removes 41 doublets to retain 3,151 cells for downstream analysis.",
      technology:
        "Chromium Next GEM Single Cell 3′ v3.1 (10x Genomics), after methanol fixation.",
      evidence: [
        {
          label: "Version-of-record paper",
          url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC13056360/",
          supports:
            "3,192 post-QC cells, 41 doublets removed, 3,151 final singlets, chemistry and analysis workflow.",
        },
        {
          label: "NCBI PRJNA1200704",
          url: "https://www.ncbi.nlm.nih.gov/bioproject/PRJNA1200704",
          supports: "BioProject, BioSample and SRA study provenance.",
        },
        {
          label: "Zenodo 18760090",
          url: "https://zenodo.org/records/18760090",
          supports: "Seventeen author-deposited files, including two technical matrices.",
        },
        {
          label: "TrypCell workflow audit",
          url: "/datasets/prjna1200704/",
          supports:
            "Raw read, matrix, checksum and paper-versus-code threshold audit.",
        },
      ],
      auditNote:
        "AMA/CDT parasites were obtained from infected rat H9c2 myoblasts (ATCC CRL-1446); the SRA repository field “isolation source: Axenic culture” is not treated as the experimental sample origin. The paper and public code use different nUMI/nGene thresholds. TrypCell reports the discrepancy and does not reconstruct a final UMAP because final author cell metadata and embeddings are not deposited.",
    },
  ],
} as const;
